import React, { FC, useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { HeartIcon } from '@/atoms/Icons/HeartIcon'
import { createHeartReaction, Heart, HeartReaction } from '@/molecules/VideoReactionButton/HeartReaction'
import { TIMELINE_ROLLUP_INTERVAL_MILLIS, useAngelPlayerContext } from '@/organisms/AngelPlayer'
import { closestMarker } from '@/organisms/AngelPlayer/SeekbarMarkerFactory'
import { useCollectiblesPlayer } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerContext'
import { useUser } from '@/services/UserService'
import { useSafeTrack } from '@/utils/analytics'
import { cLogger } from '@/utils/logging/client-logger'

interface VideoReactionButtonProps {
  className?: string
  brightMode?: boolean
  onClick?: () => void
}

const HEART_ANIMATION_MILLIS = 2000 // based on animation in Tailwind config

function heartLivedLongerThanAnimationTime(heart: Heart) {
  return new Date().getTime() - heart.createdAt < HEART_ANIMATION_MILLIS
}

export const VideoReactionButton: FC<VideoReactionButtonProps> = ({ brightMode = true, className, onClick }) => {
  const [hearts, setHearts] = useState<Heart[]>([])
  const { asset, controller, uiManager } = useAngelPlayerContext()
  const { getCurrentMoment, saveReaction } = useCollectiblesPlayer()
  const track = useSafeTrack()
  const { userId } = useUser()

  useEffect(() => {
    const id = setInterval(() => {
      setHearts((current) => {
        return current.filter(heartLivedLongerThanAnimationTime)
      })
    }, HEART_ANIMATION_MILLIS)

    return () => clearInterval(id)
  }, [])

  const showHeartReaction = useCallback(
    (reactedAt: number, momentId: string) => {
      const { diff } = closestMarker(hearts, reactedAt)

      if (diff < TIMELINE_ROLLUP_INTERVAL_MILLIS) return

      const newTimelineMarker = {
        time: controller.getCurrentTime(),
        markerType: 'heart',
      }

      uiManager?.addTimelineMarker(newTimelineMarker)

      setHearts((current) => {
        return [...current, createHeartReaction(reactedAt, momentId)]
      })
    },
    [controller, hearts, uiManager],
  )

  const handleOnClick = useCallback(() => {
    onClick?.()

    if (!userId) {
      return
    }

    const currentMoment = getCurrentMoment()

    if (currentMoment && currentMoment?.id && asset?.sourceConfig) {
      const reactedAt = Math.round(controller.getCurrentTime() * 1000)
      const momentId = currentMoment.id

      const payload = {
        videoGuid: asset.sourceConfig.guid,
        reactedAt,
        momentId,
      }

      showHeartReaction(reactedAt, momentId)
      saveReaction(payload)
      setHearts((current) => [...current, createHeartReaction(reactedAt, momentId)])

      track('Video Reaction Clicked', payload)
    } else {
      cLogger().warn('Favoriting a moment not completed missing the moment or the asset info', {
        moment: currentMoment,
        asset,
      })
    }
  }, [saveReaction, showHeartReaction, asset, controller, getCurrentMoment, onClick, setHearts, track, userId])

  const backgroundClasses = brightMode
    ? 'bg-white group-reaction-hover:bg-[#FEEEEC] group-reaction-active:bg-[#FCB2A5]'
    : 'bg-white/20 backdrop-blur-md'
  const heartColor = brightMode ? 'error-500' : 'white'

  return (
    <div className={classNames('relative rounded-full group-reaction', className)}>
      {hearts.map((heart) => (
        <HeartReaction className="z-50" key={heart.id} heart={heart} />
      ))}

      <Button
        onClick={handleOnClick}
        className={classNames(
          backgroundClasses,
          'drop-shadow-2xl',
          'flex-shrink-0',
          'p-4',
          'group-reaction-active:-scale-[0.75]',
          'group-reaction-active:rotate-180',
          'rounded-full',
        )}
        variant="plain"
      >
        <HeartIcon size={26} color1={heartColor} color2={heartColor} />
      </Button>
    </div>
  )
}
